/* eslint-disable @typescript-eslint/indent */
/* eslint-disable @typescript-eslint/brace-style */
/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useState } from 'react'
import Box from '@mui/material/Box'
import { FormProvider, useForm, type SubmitHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import ValidationAlert from '../../../../components/ValidationAlert'
import { bindSetError, showServerFieldErrors } from '../../../../utils/showServerFieldErrors'
import _ from 'lodash'
import FormInput from '../../../../components/FormElements/FormInput'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import FreeSoloMultiChips from '../../../../components/FormElements/FreeSoloMultiChips'
import { useMutation } from '@tanstack/react-query'
import api from '../../../../utils/api'
import { ATTRIBUTE_TYPES, READONLY_TOOLTIP } from '../../../../consts'
import { type IAttribute } from '../../../../models/Attributes'
import { notification } from '../../../../components/Notifications'
import { LastUpdateMeta } from '../../../../components/GenericModal/ChangeMetaInfo'
import { useAccess } from '../../../../hooks/useAccess'
import ReportEnabledToggle from '../../ReportEnabledToggle'

interface FormType {
  attributeKey: string
  attributeType: string
  attributeValues: string[]
  isReportEnabled?: boolean
}

interface CreateAndEditCourseAttributesProps {
  selectedRow?: IAttribute | null
  selectedRowWithActions?: any
  handleCloseModal?: () => void
  refetch: () => void
}

export const CreateAndEditCourseAttributes: React.FC<CreateAndEditCourseAttributesProps> = ({ refetch, selectedRow, selectedRowWithActions, handleCloseModal = () => { } }): JSX.Element => {
  const { readonly } = useAccess(selectedRowWithActions?.actions ?? [])
  const [errors, setErrors] = useState<unknown>({})
  const isEditAction = selectedRow !== undefined

  const { attributeKey, attributeValues, isReportEnabled } = selectedRow ?? {}

  const termFormSchema = yup.object().shape({
    attributeKey: yup.string().required('Title  is required'),
    attributeValues: yup.array().of(yup.string())
  })

  const methods = useForm<any>({
    resolver: yupResolver(termFormSchema),
    defaultValues: {
      attributeKey,
      attributeValues,
      // Backwards compat: only an explicit false is "off". On edit, a legacy
      // null/undefined reads as on (enabled = value !== false); new attributes
      // default off.
      isReportEnabled: isEditAction ? isReportEnabled !== false : false
    }
  })
  const { handleSubmit, control, formState: { isDirty } } = methods

  const mutation = useMutation({
    meta: { silent: true },
    mutationFn: async (data: FormType) => {
      const payload = {
        attributeKey: data.attributeKey,
        attributeValues: data.attributeValues,
        attributeType: ATTRIBUTE_TYPES.COURSE_ATTRIBUTES,
        isReportEnabled: !!data.isReportEnabled
      }

      if (isEditAction) {
        const response = await api.patch(`/api/attributes/${selectedRow?.attributeId}`, payload)
        return response.data
      } else {
        const response = await api.post('/api/attributes/', payload)
        return response.data
      }
    },
    onSuccess: (res: any) => {
      const message = isEditAction ? 'Successfully updated' : 'Successfully created'
      notification(message, { variant: 'success', subTitle: res?.attributeKey })
    },
    onError: (error: unknown) => {
      setErrors(showServerFieldErrors(error, bindSetError(methods.setError), methods.clearErrors))
    }
  })

  const onSubmit: SubmitHandler<FormType> = async (data) => {
    try {
      mutation.mutate(data, {
        onSuccess: () => {
          handleCloseModal()
          refetch()
        },
        onError: (error) => {
          console.error('Error during submit:', error)
        }
      })
    } catch (error) {
      console.error('Error during submit:', error)
    }
  }

  return (
    <FormProvider {...methods}>
      <ValidationAlert errors={errors} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display='flex' flexDirection='column' gap={6}>
          <FormInput
            name="attributeKey"
            label="Title"
            placeholder='Attribute title'
            control={control}
            required
          />
          <FreeSoloMultiChips placeholder='Attribute values' name='attributeValues' label='Values' />
        </Box>
        <ReportEnabledToggle />

        <DefaultModalActions
          destructiveButtonProps={{
            onClick: handleCloseModal
          }}
          confirmButtonProps={{
            text: isEditAction ? 'Save' : 'Create',
            loading: mutation.isPending,
            type: 'submit',
            disabled: readonly || (isEditAction && !isDirty),
            tooltip: readonly ? READONLY_TOOLTIP : undefined
          }}
          lastUpdateMeta={isEditAction && (
            <LastUpdateMeta lastEditedBy={selectedRow?.modifiedByUsername} lastEditedAt={selectedRow?.modifiedOn} />
          )}
        />
      </form>
    </FormProvider>
  )
}
